import { Component, input, inject, signal, effect, computed } from '@angular/core';
import { forkJoin } from 'rxjs';
import { BlastDashboardComponent }     from './blast-dashboard/blast-dashboard.component';
import { BlastFilesComponent }         from './blast-files/blast-files.component';
import { BlastJobHistoryComponent }    from './blast-job-history/blast-job-history.component';
import { BlastKpiComponent }           from './blast-kpi/blast-kpi.component';
import { BlastHolesComponent }      from './blast-holes/blast-holes.component';
// import { FragmentationComponent } from './fragmentation_new/fragmentation_new.component';
import { BlastVibrationComponent }     from './blast-vibration/blast-vibration.component';
import { BlastEnvironmentComponent }   from './blast-environment/blast-environment.component';
import { CustomerFeedbackComponent }   from './customer-feedback/customer-feedback.component';
import { SupplierFeedbackComponent }   from './supplier-feedback/supplier-feedback.component';
import { SurveyService }               from 'src/app/services/survey.service';
import { KeycloakUtilisateurService }  from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { KpiService }                  from 'src/app/services/kpi.service';
import { UtilisateurService }          from 'src/app/services/utilisateur.service';
import { FonctionsService }            from 'src/app/services/fonctions-partagees.service';
import { ConfigurationSaisieDonneesAnalyse } from 'src/app/bo/configurationSaisieDonneesAnalyse';
import { DonneeAnalyse }               from 'src/app/bo/donneeAnalyse';
import { AccessPermissionType }        from 'src/app/bo/accessPermisionType';
import { ItemSortHelper }              from 'src/app/utils/itemSortHelper';
import { Blast, HoleService }          from 'src/app/services/hole.service';
import { FragmentationComponent } from './fragmentation/fragmentation.component';
import { BlastService }                from 'src/app/services/blast.service';
import { LegacyHostComponent }         from 'src/app/shared/legacy/legacy-host.component';
import { LegacySiteContextService }    from 'src/app/services/new_services/legacy-site-context.service';
import { isLegacyBlastTab }            from './blast-tabs';
import { LegacyBlastLoadHaulComponent }   from 'src/app/shared/legacy/blast/legacy-blast-load-haul.component';
import { LegacyBlastFleetComponent }      from 'src/app/shared/legacy/blast/legacy-blast-fleet.component';

@Component({
  selector:    'app-blast',
  standalone:  true,
  templateUrl: './blast.component.html',
  styles: [':host { display: flex; flex-direction: column; flex: 1; min-height: 0;}'],
  imports: [
    BlastDashboardComponent,
    BlastFilesComponent,
    BlastJobHistoryComponent,
    BlastKpiComponent,
    BlastHolesComponent,
    FragmentationComponent,
    BlastVibrationComponent,
    BlastEnvironmentComponent,
    CustomerFeedbackComponent,
    SupplierFeedbackComponent,
    LegacyHostComponent,
    LegacyBlastLoadHaulComponent,
    LegacyBlastFleetComponent,
  ],
})
export class BlastComponent {
  blastId     = input.required<number>();
  activeTabId = input.required<string>();
  regionId    = input.required<number>();

  protected readonly legacy = inject(LegacySiteContextService);

  protected readonly isLegacyTab = computed(() => isLegacyBlastTab(this.activeTabId()));

  private readonly surveyService              = inject(SurveyService);
  private readonly keycloakUtilisateurService = inject(KeycloakUtilisateurService);
  private readonly kpiService                 = inject(KpiService);
  private readonly utilisateurService         = inject(UtilisateurService);
  private readonly fonctionsService           = inject(FonctionsService);
  private readonly holeService                = inject(HoleService);
  private readonly blastService               = inject(BlastService);

  protected surveyJsonForm: any;
  protected readonly customerSurveyResponses = signal<any>(undefined);
  protected readonly hasWriteCustomerSurvey = signal(false);
  protected readonly hasReadCustomerSurvey  = signal(false);
  protected readonly supplierSurveyResponses = signal<any>(undefined);
  protected readonly hasWriteSupplierSurvey = signal(false);
  protected readonly hasReadSupplierSurvey  = signal(false);
  protected readonly blast                  = signal<Blast | undefined>(undefined);

  protected readonly initAnalyseFaite = signal(false);
  protected readonly kpiIsVerified    = signal(false);

  protected readonly donneesAnalyseBlast = signal<DonneeAnalyse[]>([]);
  protected readonly configSaisieSite    = signal<ConfigurationSaisieDonneesAnalyse[]>([]);

  constructor() {
    // Reset all cached state when blastId changes so downstream effects re-trigger.
    effect(() => {
      this.blastId();
      this.initAnalyseFaite.set(false);
      this.blast.set(undefined);
      this.customerSurveyResponses.set(undefined);
      this.supplierSurveyResponses.set(undefined);
      this.donneesAnalyseBlast.set([]);
      this.configSaisieSite.set([]);
      this.kpiIsVerified.set(false);
    });

    // Load feedback responses whenever blast changes or feedback tab becomes active.
    effect(() => {
      const blastId = this.blastId();
      if (this.activeTabId() === 'feedback' && blastId) {
        this.chargerCustomerSurveyResponses();
      }
      if (this.activeTabId() === 'supplier-feedback' && blastId) {
        this.chargerSupplierSurveyResponses();
      }
    });

    effect(() => {
      const regionId = this.regionId();
      if (regionId) {
        const userRoles = this.keycloakUtilisateurService.allRoles;
        this.surveyService.getSurveyQuestionsJsonData(regionId).then((questions) => {
          this.surveyJsonForm = questions;
          this.setSurveyRoles(this.surveyJsonForm, userRoles);
        });
      }
    });

    effect(() => {
      const siteId = this.utilisateurService.currentSite()?.siteId;
      if (this.activeTabId() === 'kpi' && siteId && !this.initAnalyseFaite()) {
        this.chargerDonneesAnalyse();
      }
    });

    effect(() => {
      if (this.activeTabId() === 'environment' && !this.blast()) {
        this.holeService.GetBlastHolePositions(this.blastId()).then((blast: Blast) => {
          this.blast.set(blast);
        });
      }
    });
  }

  protected chargerDonneesAnalyse(): void {
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    const siteId = this.utilisateurService.currentSite()?.siteId;
    const isGuest = !this.keycloakUtilisateurService.allRoles.includes('access_all_kpi');

    forkJoin([
      this.kpiService.getBlastAnalysisData(this.blastId()),
      this.kpiService.getSiteConfigAnalysisEntry(siteId),
      this.blastService.getBlast(this.blastId()),
    ]).subscribe({
      next: async (resultats) => {
        resultats[0].forEach(donnee => {
          const config = resultats[1].find(c => c.referentielDonneeAnalyseId === donnee.referentielDonneeAnalyseId);
          if (config) {
            donnee.accessPermission = config.accessPermission;
            donnee.isEnabled = config.isEnabled;
            donnee.autorisePourUtil = (isGuest && config.accessPermission === AccessPermissionType.READANDWRITE) || (!isGuest && config.isEnabled);
          }
        });

        resultats[0].sort((a, b) => {
          if (a.autorisePourUtil === b.autorisePourUtil)
            return a.referentielDonneeAnalyse.nom.localeCompare(b.referentielDonneeAnalyse.nom);
          return a.autorisePourUtil ? -1 : 1;
        });

        try { resultats[0] = await ItemSortHelper.sortKpi(resultats[0]); } catch { }

        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
        this.donneesAnalyseBlast.set(resultats[0]);
        this.configSaisieSite.set(resultats[1]);
        this.kpiIsVerified.set(!!resultats[2]?.kpiIsVerified);
        this.initAnalyseFaite.set(true);
      },
      error: () => {
        clearTimeout(startLoader);
        this.fonctionsService.loading_screen(false);
      },
    });
  }

  protected onKpiVerifiedChange(isVerified: boolean): void {
    try {
      const previous = this.kpiIsVerified();
      this.kpiIsVerified.set(isVerified);
      const obs = this.kpiService.setBlastKpiVerified(this.blastId(), isVerified);
      obs.subscribe({
        error: (err) => {
          console.error('[blast.component] Échec de la mise à jour du KPI vérifié :', err);
          this.kpiIsVerified.set(previous);
          this.fonctionsService.launch_toast(false);
        }
      });
    } catch (e) {
      console.error('[blast.component] Erreur inattendue lors de la mise à jour du KPI vérifié :', e);
    }
  }

  protected chargerCustomerSurveyResponses(): void {
    this.surveyService.getSurveyResponsesFromApi(this.blastId()).then((responses) => {
      this.customerSurveyResponses.set(responses);
    });
  }

  protected chargerSupplierSurveyResponses(): void {
    this.surveyService.getSurveyResponsesFromApi(this.blastId()).then((responses) => {
      this.supplierSurveyResponses.set(responses);
    });
  }

  private setSurveyRoles(jsonForm: any, userRoles: string[]): void {
    if (jsonForm) {
      this.hasReadCustomerSurvey.set(jsonForm.customerSurvey?.permission?.read?.some((r: string) => userRoles.includes(r)) ?? false);
      this.hasWriteCustomerSurvey.set(jsonForm.customerSurvey?.permission?.write?.some((r: string) => userRoles.includes(r)) ?? false);

      this.hasReadSupplierSurvey.set(jsonForm.providerSurvey?.permission?.read?.some((r: string) => userRoles.includes(r)) ?? false);
      this.hasWriteSupplierSurvey.set(jsonForm.providerSurvey?.permission?.write?.some((r: string) => userRoles.includes(r)) ?? false);
    }
  }
}
